import { toast } from "react-hot-toast";
import {
  Form,
  LoaderFunctionArgs,
  ActionFunctionArgs,
  useLoaderData,
  redirect,
  useFetcher,
} from "react-router-dom";
import IndeterminateCheckbox from "../../../components/inputs/IndeterminantCheckbox";
import { Input, TextArea } from "../../../components/inputs/Input";
import CustomSelect from "../../../components/customSelect";
import { FormLayout } from "../../../layout/FormLayout";
import { ShowLayout } from "../../../layout/ShowLayout";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { GET_LIST_EVENTS_BY_ORGANIZATION } from "../../../queries";
import { organizationStore } from "../../../store/organization";
import { Attendee } from "../../../types/attendee";
import { Event } from "../../../types/event";
import { UPDATE_ATTENDEE } from "./mutations";
import {
  GET_ATTENDEE,
  GET_ATTENDEE_CARD,
  GET_ATTENDEE_ORDERS,
  GET_ATTENDEE_ORDERS_BY_ATTENDEE,
  GET_ATTENDEE_RFID,
} from "./queries";
import DeleteModal from "../../../components/DeleteModal";
import { SecondaryButton } from "../../../components/Button";
import { usePermissions } from "../../auth/api";
import React, { useEffect, useRef, useState } from "react";
import { GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { rfidColDefs } from "../components/rfidAssetsHelpers";
import { cardsOnFileColDefs } from "../components/cardsOnFileHelpers";
import {
  CardsOnFileRow,
  RFIDAssetsRow,
  TransactionReportRow,
} from "../components/type";
import Loader from "../../../components/Loader";
import { ordersColDefs } from "../components/ordersHelpers";
import { userStore } from "../../../store/user";
import { printBrandedQR } from "../components/printBrandedQr";
import { BWLogoBase64 } from "../../../constants";
import { useEventStore } from "../../../store/event";
import { useUserStore } from "../../../store/user";

const client = createApolloClient();

const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

export const editTicketsLoader = async ({ params }: LoaderFunctionArgs) => {
  const organizationId = getState().organizationId;
  const user = getUserStore().user;

  let where: any = {
    organization_id: { _eq: organizationId },
  };
  if (user?.events) {
    where = {
      ...where,
      id: {
        _in: user?.events,
      },
    };
  }
  const [{ data: attendeeData }, { data: eventsData }] = await Promise.all([
    client.query({
      query: GET_ATTENDEE,
      variables: {
        id: params?.id,
      },
    }),
    client.query({
      query: GET_LIST_EVENTS_BY_ORGANIZATION,
      variables: {
        where,
      },
    }),
  ]);
  return {
    attendee: attendeeData.attendees_by_pk,
    events: eventsData.events,
  };
};

export const editTicketsAction = async ({
                                           params,
                                           request,
                                         }: ActionFunctionArgs) => {
  const { id } = params;
  const body = await request.formData();
  const bodyData = Object.fromEntries(body);
  try {
    await client.mutate({
      mutation: UPDATE_ATTENDEE,
      variables: {
        input: bodyData,
        where: {
          id: { _eq: id },
        },
      },
    });
    toast.success("Attendee updated successfully");
    return redirect(`/attendees/${params.id}`);
  } catch (error) {
    toast.error("Error updating attendee");
  }
};

export const TicketEdit = () => {
  const rfidGridRef = useRef<GridApi>();
  const cardGridRef = useRef<GridApi>();
  const orderGridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  const [isLoadingRFID, setIsLoadingRFID] = useState(true);
  const [isLoadingCard, setIsLoadingCard] = useState(true);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const { attendee, events } = useLoaderData() as {
    attendee: Attendee;
    events: Event[];
  };
  const [orderRowData, setOrderRowData] = useState<TransactionReportRow[]>([]);
  const [cardRowData, setCardRowData] = useState<CardsOnFileRow[]>([]);
  const [rfidRowData, setRfidRowData] = useState<RFIDAssetsRow[]>([]);
  const [attendeeOrderData, setAttendeeOrderData] = useState<
      TransactionReportRow[]
  >([]);
  const [rfidOrderData, setRfidOrderData] = useState<TransactionReportRow[]>(
      []
  );
  const fetcher = useFetcher();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rfidData] = await Promise.all([
          client.query({
            query: GET_ATTENDEE_RFID,
            variables: {
              id: attendee?.id,
              event_id: attendee?.event_id,
            },
          }),
        ]);
        setRfidRowData(rfidData.data.rfid_assets);
      } catch (error) {
        toast.error("Error Fetching RFID Data");
      } finally {
        setIsLoadingRFID(false);
      }
    };
    fetchData();
  }, [attendee]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cardData] = await Promise.all([
          client.query({
            query: GET_ATTENDEE_CARD,
            variables: {
              id: attendee?.id,
              event_id: attendee?.event_id,
            },
          }),
        ]);
        setCardRowData(cardData.data.card_on_files);
      } catch (error) {
        toast.error("Error Fetching Card Data");
      } finally {
        setIsLoadingCard(false);
      }
    };
    fetchData();
  }, [attendee]);

  // Fetch orders based on rfidRowData
  useEffect(() => {
    const fetchRfidOrders = async (uids: string[]) => {
      try {
        const orderData = await client.query({
          query: GET_ATTENDEE_ORDERS,
          variables: {
            uids,
            event_id: attendee?.event_id,
          },
        });
        setRfidOrderData(orderData.data.reports_transactions_ticketing);
      } catch (error) {
        toast.error("Error Fetching RFID Order Data: " + error);
      }
    };

    if (rfidRowData.length > 0) {
      const uids = rfidRowData.map((row) => row.uid);
      fetchRfidOrders(uids);
    }
  }, [rfidRowData, attendee?.event_id]);

  // Fetch orders based on attendee data
  useEffect(() => {
    const fetchAttendeeOrders = async () => {
      try {
        const orderData2 = await client.query({
          query: GET_ATTENDEE_ORDERS_BY_ATTENDEE,
          variables: {
            attendeeId: attendee?.id,
            event_id: attendee?.event_id,
          },
        });
        setAttendeeOrderData(orderData2.data.reports_transactions_ticketing);
      } catch (error) {
        toast.error("Error Fetching Attendee Order Data: " + error);
      }
    };

    if (attendee?.id && attendee?.event_id) {
      fetchAttendeeOrders();
    }
  }, [attendee]);

  // Combine the two datasets
  useEffect(() => {
    if ((attendeeOrderData?.length ?? 0) > 0 || (rfidOrderData?.length ?? 0) > 0) {
      const combinedOrderData = [
        ...attendeeOrderData,
        ...rfidOrderData,
      ];

      const uniqueOrders = Array.from(
          new Set(combinedOrderData.map(order => order.order_id))
      ).map(order_id =>
          combinedOrderData.find(order => order.order_id === order_id)
      ).filter(order => order !== undefined); // Filter out undefined orders

      setOrderRowData(uniqueOrders as TransactionReportRow[]); // Cast the array type
      setIsLoadingOrder(false);
    }
  }, [attendeeOrderData, rfidOrderData]);

  const { deletePermission } = usePermissions("attendees");

  // Get event and user info from stores
  const { allEvents, eventId } = useEventStore();
  const { user } = useUserStore();

  const handlePrintAllTickets = async () => {
    if (rfidRowData.length === 0) {
      toast.error("No tickets to print");
      return;
    }

    const username = user?.username || user?.email || "";

    try {
      toast.loading(`Preparing ${rfidRowData.length} ticket(s) for printing...`);
      const ticketImages: string[] = [];

      for (let i = 0; i < rfidRowData.length; i++) {
        const data = rfidRowData[i];
        const { uid } = data;
        const tokensBalance = data?.tokens_balance;
        console.log({data})
        const eventName = data.event.name;
        let tokenBalanceString = '';

        if (tokensBalance && Object.keys(tokensBalance).length > 0) {
          const tokenStrings = Object.values<any>(tokensBalance).map(
            (token) => `${token.redeemable_token_name} - ${token.balance}`
          );
          tokenBalanceString = tokenStrings.join(", ");
        }

        const { generatePrintBrandedQRDataUrl } = await import("../components/printBrandedQr");

        const dataUrl = await generatePrintBrandedQRDataUrl({
          uid: String(uid ?? ""),
          eventName: eventName,
          accessType: data.access_type,
          tokenBalance: tokenBalanceString,
          printedBy: username,
          logoBase64: BWLogoBase64,
        });

        ticketImages.push(dataUrl);
      }

      toast.dismiss();

      // Create a single print window with all tickets as separate pages
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        throw new Error("Could not open print window. Please allow pop-ups for this site.");
      }

      // Build HTML with all tickets, each on its own page
      const ticketImagesHtml = ticketImages.map((dataUrl, index) => `
        <div class="ticket-page" style="page-break-after: ${index < ticketImages.length - 1 ? 'always' : 'auto'};">
          <img src="${dataUrl}" alt="Ticket ${index + 1}" />
        </div>
      `).join('\n');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Tickets</title>
            <style>
              @page {
                size: 2in 5.5in;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
              }
              .ticket-page {
                width: 2in;
                height: 5.5in;
                display: flex;
                justify-content: center;
                align-items: center;
                page-break-after: always;
              }
              .ticket-page:last-child {
                page-break-after: auto;
              }
              img {
                width: 2in;
                height: 5.5in;
                display: block;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            ${ticketImagesHtml}
            <script>
              window.onload = function() {
                window.print();
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();

      toast.success(`${rfidRowData.length} ticket(s) ready to print`);
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to prepare tickets: " + error);
    }
  };

  return (
      <div>
        <Form action={`/attendees/${attendee.id}/edit`} method="put">
          {deletePermission && (
              <div className="flex">
                <div className="ml-auto py-2">
                  <DeleteModal
                      onConfirm={() => {
                        fetcher.submit(
                            {},
                            {
                              method: "delete",
                              action: `/attendees/${attendee.id}/delete`,
                            }
                        );
                      }}
                  >
                    {({ setOpen }) => (
                        <SecondaryButton onClick={() => setOpen(true)}>
                          Delete
                        </SecondaryButton>
                    )}
                  </DeleteModal>
                </div>
              </div>
          )}
          <FormLayout>
            <div className="col-span-6 sm:col-span-4">
              <Input
                  label="Email"
                  name="email"
                  defaultValue={attendee?.email}
                  updateValue
              />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <IndeterminateCheckbox
                  name="is_suspended"
                  label="Is Suspended"
                  defaultChecked={attendee.is_suspended}
              />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <IndeterminateCheckbox
                  name="is_active"
                  label="Is Active"
                  defaultChecked={attendee.is_active}
              />
            </div>
            <div className="col-span-6">
              <Input
                  label="First Name"
                  name="first_name"
                  defaultValue={attendee.first_name}
              />
            </div>
            <div className="col-span-6">
              <Input
                  label="Last Name"
                  name="last_name"
                  defaultValue={attendee.last_name}
              />
            </div>
            <div className="col-span-6">
              <Input
                  label="Business Name"
                  name="last_name"
                  defaultValue={attendee.business_name}
              />
            </div>
            <div className="col-span-6">
              <Input
                  label="Phone Number"
                  name="phone_number"
                  defaultValue={attendee.phone_number}
              />
            </div>
            <div className="col-span-6">
              <CustomSelect
                  title="Event"
                  name="event_id"
                  defaultValue={attendee.event_id}
                  options={[
                    ...events.map((event) => ({
                      label: event.name,
                      value: event.id,
                    })),
                  ]}
              />
            </div>
            <div className="col-span-6 sm:col-span-6">
              <TextArea name="note" label="Note" defaultValue={attendee.note}/>
            </div>
          </FormLayout>
        </Form>
        <div className="mt-2">
          <ShowLayout
            header="Tickets"
            actions={
              <div className="flex gap-2">
                <SecondaryButton onClick={() => rfidGridRef.current?.exportDataAsCsv({ fileName: "Tickets.csv", columnSeparator: ",", allColumns: true })}>
                  Export to CSV
                </SecondaryButton>
                <SecondaryButton
                    onClick={handlePrintAllTickets}
                    isDisabled={isLoadingRFID || rfidRowData.length === 0}
                >
                  Print All ({rfidRowData.length})
                </SecondaryButton>
              </div>
            }
          >
            <div style={{height: "25vh"}}>
              {isLoadingRFID ? (
                  <Loader/>
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={rfidRowData}
                        columnDefs={rfidColDefs}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (rfidGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
        <div className="mt-2">
          <ShowLayout
            header="Cards On File"
            actions={
              <SecondaryButton onClick={() => cardGridRef.current?.exportDataAsCsv({ fileName: "CardsOnFile.csv", columnSeparator: ",", allColumns: true })}>
                Export to CSV
              </SecondaryButton>
            }
          >
            <div style={{ height: "25vh" }}>
              {isLoadingCard ? (
                  <Loader />
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={cardRowData}
                        columnDefs={cardsOnFileColDefs}
                        rowHeight={rowHeight}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (cardGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
        <div className="mt-2">
          <ShowLayout
            header="Orders"
            actions={
              <SecondaryButton onClick={() => orderGridRef.current?.exportDataAsCsv({ fileName: "Orders.csv", columnSeparator: ",", allColumns: true })}>
                Export to CSV
              </SecondaryButton>
            }
          >
            <div style={{ height: "25vh" }}>
              {isLoadingOrder ? (
                  <Loader />
              ) : (
                  <div
                      className={"ag-theme-quartz"}
                      style={{ width: "100%", height: "100%" }}
                  >
                    <AgGridReact
                        rowData={orderRowData}
                        columnDefs={ordersColDefs}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination={true}
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (orderGridRef.current = params.api)}
                    />
                  </div>
              )}
            </div>
          </ShowLayout>
        </div>
      </div>
  );
};
